/* ============================================================================
   <rtb-read-more> — a paragraph that shows its first lines and a toggle for
   the rest. Used by the About the brand band (sections/rtb-product-story).

   Load it with `defer` (classic script, not a module):
       <script src="{{ 'rtb-read-more.js' | asset_url }}" defer></script>

   WHY IT EXISTS
   The band is drawn to a fixed composition: a plate, a heading, a few lines
   of copy and a photograph beside them, with the next band's wave rising over
   the bottom-left corner. Brand stories run from two sentences to a
   paragraph, and a long one would push the column past the photo and into
   the wave. So the copy shows a set number of lines, and the rest is one
   click away.

   THE CLAMP IS SET BY THIS SCRIPT, NEVER BY THE MARKUP. Without JavaScript the
   whole text shows and the toggle stays hidden: a clamp with no way to open it
   would lose the text for nobody's benefit. With JavaScript, the clamp goes on,
   the text is measured, and if it fits the clamp comes straight back off and
   the toggle never appears.

   The full text is in the DOM at all times, so search engines and screen
   readers get all of it whichever state the eye sees. The toggle carries
   aria-expanded and aria-controls, and its label comes from the markup
   (data-more-label / data-less-label), so it is editable in the theme editor;
   a blank label falls back to the toggle's server-rendered text.

   EXPECTED MARKUP
     <rtb-read-more data-more-label="Read more" data-less-label="Show less">
       <div id="x" data-rtb-read-more-body>…</div>
       <button type="button" data-rtb-read-more-toggle aria-controls="x"
               aria-expanded="false" hidden>Read more</button>
     </rtb-read-more>
   ============================================================================ */
(() => {
  'use strict';

  if (customElements.get('rtb-read-more')) return;

  class RtbReadMore extends HTMLElement {
    connectedCallback() {
      this.body = this.querySelector('[data-rtb-read-more-body]');
      this.toggleButton = this.querySelector('[data-rtb-read-more-toggle]');
      if (!this.body || !this.toggleButton) return;

      // The server-rendered label, kept for a data-*-label left blank.
      this.fallbackLabel ??= this.toggleButton.textContent.trim();

      this.onClick = () => this.toggle();
      this.toggleButton.addEventListener('click', this.onClick);

      // Re-measure only when the WIDTH changes (a narrower column wraps into
      // more lines). Height changes are this element's own doing; reacting to
      // them would let the clamp and the observer feed each other.
      this.lastWidth = 0;
      this.observer = new ResizeObserver((entries) => {
        const width = Math.round(entries[0].contentRect.width);
        if (width === this.lastWidth) return;
        this.lastWidth = width;
        this.measure();
      });
      this.observer.observe(this);

      // Measure now too: a ResizeObserver only reports on a rendered frame,
      // which a background tab never draws. And again once the web fonts
      // land, since the fallback face wraps differently. The token makes a
      // promise that settles after disconnect a no-op.
      const token = {};
      this.connection = token;
      this.measure();
      document.fonts?.ready.then(() => {
        if (this.connection === token) this.measure();
      });
    }

    disconnectedCallback() {
      this.connection = null;
      this.toggleButton?.removeEventListener('click', this.onClick);
      this.observer?.disconnect();
      this.observer = null;
    }

    measure() {
      if (!this.isConnected || this.hasAttribute('data-expanded')) return;
      this.setAttribute('data-clamp', '');
      const overflowing = this.body.scrollHeight > this.body.clientHeight + 1;
      if (!overflowing) this.removeAttribute('data-clamp');
      this.toggleButton.hidden = !overflowing;
    }

    toggle() {
      const expand = !this.hasAttribute('data-expanded');
      this.toggleAttribute('data-expanded', expand);
      this.toggleAttribute('data-clamp', !expand);
      this.toggleButton.setAttribute('aria-expanded', String(expand));
      const label = expand ? this.dataset.lessLabel : this.dataset.moreLabel;
      this.toggleButton.textContent = label || this.fallbackLabel || this.toggleButton.textContent;
    }
  }

  customElements.define('rtb-read-more', RtbReadMore);
})();
